/**
 * Production Batches Hook
 * Provides production batch management operations with React Query
 *
 * @description
 * This hook provides:
 * - Fetch production batches with pagination and filters
 * - Fetch single production batch by ID with inputs and timeline
 * - Fetch available transactions for material selection
 * - Fetch production batch summary statistics
 * - Create, start, complete, and cancel batch mutations
 * - Automatic cache invalidation
 *
 * @author SAUBHAGYA Development Team
 * @since Story 5-5
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  manufacturingApi,
  ProductionBatchDTO,
  AvailableTransactionDTO,
  ProductionBatchSummary,
  CreateBatchRequest,
  CompleteBatchRequest,
  PaginatedResponse,
} from '@/services/manufacturing/manufacturingApi';

/**
 * Filters for production batch list
 */
export interface ProductionBatchFilters {
  page?: number;
  size?: number;
  manufacturingUnitId?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

/**
 * Hook to fetch production batches with optional filters
 * @param filters - Optional filters
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with paginated production batches
 */
export function useProductionBatches(
  filters?: ProductionBatchFilters,
  enabled: boolean = true
) {
  // Create a stable query key by serializing filter values
  const queryKey = [
    'production-batches',
    filters?.page ?? 0,
    filters?.size ?? 10,
    filters?.manufacturingUnitId,
    filters?.status,
    filters?.startDate,
    filters?.endDate,
    filters?.search,
  ];

  return useQuery<PaginatedResponse<ProductionBatchDTO>>({
    queryKey,
    queryFn: () => manufacturingApi.getProductionBatches(filters),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch a single production batch by ID
 * @param batchId - Production batch ID (null to disable query)
 * @param enabled - Whether to enable the query (default: true when batchId is provided)
 * @returns Query result with production batch details
 */
export function useProductionBatch(
  batchId: number | null,
  enabled: boolean = true
) {
  return useQuery<ProductionBatchDTO>({
    queryKey: ['production-batch', batchId],
    queryFn: () => manufacturingApi.getProductionBatch(batchId!),
    enabled: enabled && batchId !== null && batchId > 0,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch available transactions for a manufacturing unit
 * @param manufacturingUnitId - Manufacturing unit ID (null to disable query)
 * @param enabled - Whether to enable the query (default: true when unitId is provided)
 * @returns Query result with list of available transactions
 */
export function useAvailableTransactions(
  manufacturingUnitId?: number | null,
  enabled: boolean = true
) {
  return useQuery<AvailableTransactionDTO[]>({
    queryKey: ['available-transactions', manufacturingUnitId ?? 'auto'],
    queryFn: () => manufacturingApi.getAvailableTransactions(manufacturingUnitId ?? undefined),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch production batch summary statistics
 * @param manufacturingUnitId - Optional manufacturing unit ID filter
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with production batch summary data
 */
export function useProductionBatchSummary(
  manufacturingUnitId?: number,
  enabled: boolean = true
) {
  return useQuery<ProductionBatchSummary>({
    queryKey: ['production-batch-summary', manufacturingUnitId],
    queryFn: () => manufacturingApi.getProductionBatchSummary(manufacturingUnitId),
    enabled,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to create a new production batch
 * @returns Mutation object with createBatch function
 */
export function useCreateProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBatchRequest) =>
      manufacturingApi.createProductionBatch(data),
    onSuccess: () => {
      // Invalidate and refetch production batches list
      queryClient.invalidateQueries({ queryKey: ['production-batches'] });
      // Invalidate summary
      queryClient.invalidateQueries({ queryKey: ['production-batch-summary'] });
      // Invalidate available transactions (consumed quantities changed)
      queryClient.invalidateQueries({ queryKey: ['available-transactions'] });
    },
  });
}

/**
 * Hook to start a production batch
 * @returns Mutation object with startBatch function
 */
export function useStartProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (batchId: number) =>
      manufacturingApi.startProductionBatch(batchId),
    onSuccess: (_, batchId) => {
      // Invalidate and refetch production batches list
      queryClient.invalidateQueries({ queryKey: ['production-batches'] });
      // Invalidate specific batch
      queryClient.invalidateQueries({
        queryKey: ['production-batch', batchId],
      });
      // Invalidate summary
      queryClient.invalidateQueries({ queryKey: ['production-batch-summary'] });
    },
  });
}

/**
 * Hook to complete a production batch
 * @returns Mutation object with completeBatch function
 */
export function useCompleteProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      batchId,
      data,
    }: {
      batchId: number;
      data: CompleteBatchRequest;
    }) => manufacturingApi.completeProductionBatch(batchId, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch production batches list
      queryClient.invalidateQueries({ queryKey: ['production-batches'] });
      // Invalidate specific batch
      queryClient.invalidateQueries({
        queryKey: ['production-batch', variables.batchId],
      });
      // Invalidate summary
      queryClient.invalidateQueries({ queryKey: ['production-batch-summary'] });
    },
  });
}

/**
 * Hook to cancel a production batch
 * @returns Mutation object with cancelBatch function
 */
export function useCancelProductionBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ batchId, reason }: { batchId: number; reason?: string }) =>
      manufacturingApi.cancelProductionBatch(batchId, reason),
    onSuccess: (_, variables) => {
      // Invalidate and refetch production batches list
      queryClient.invalidateQueries({ queryKey: ['production-batches'] });
      // Invalidate specific batch
      queryClient.invalidateQueries({
        queryKey: ['production-batch', variables.batchId],
      });
      // Invalidate summary
      queryClient.invalidateQueries({ queryKey: ['production-batch-summary'] });
      // Invalidate available transactions (quantities might be released)
      queryClient.invalidateQueries({ queryKey: ['available-transactions'] });
    },
  });
}

/**
 * Hook to mark a production batch as ready for sale
 * @returns Mutation object with markReadyForSale function
 */
export function useMarkProductionBatchReadyForSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ batchId, notes }: { batchId: number; notes?: string }) =>
      manufacturingApi.markProductionBatchReadyForSale(batchId, notes),
    onSuccess: (_, variables) => {
      // Invalidate and refetch production batches list
      queryClient.invalidateQueries({ queryKey: ['production-batches'] });
      // Invalidate specific batch
      queryClient.invalidateQueries({
        queryKey: ['production-batch', variables.batchId],
      });
      // Invalidate summary
      queryClient.invalidateQueries({ queryKey: ['production-batch-summary'] });
    },
  });
}

/**
 * Hook to manually refetch production batches
 * @returns Function to refetch production batches
 */
export function useRefetchProductionBatches() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['production-batches'] });
    queryClient.invalidateQueries({ queryKey: ['production-batch-summary'] });
  };
}
